import React, { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekDays(mondayStr) {
  const days = [];
  const monday = new Date(mondayStr + 'T00:00:00');
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push({
      label: DAY_NAMES[i],
      date: d.toISOString().split('T')[0],
      shortDate: `${d.getMonth() + 1}/${d.getDate()}`
    });
  }
  return days;
}

export function WeeklyTimesheetGrid({
  timesheet, laborEntries, jobs, crewMembers, crafts, classifications,
  canEdit, token, onRefresh, getFilteredClassifications
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLineKey, setEditingLineKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingLineKey, setDeletingLineKey] = useState(null);

  // Single-line form state (used for editing)
  const [formCraftId, setFormCraftId] = useState('');
  const [formCrewMemberId, setFormCrewMemberId] = useState('');
  const [formJobId, setFormJobId] = useState('');
  const [formClassificationId, setFormClassificationId] = useState('');
  const [dailyHours, setDailyHours] = useState({});

  // Multi-select bulk add state
  const [bulkMode, setBulkMode] = useState(false);
  const [crewSearchFilter, setCrewSearchFilter] = useState('');
  // Per-person allocations: [{ crewMemberId, craftId, jobs: [{ jobId, classificationId, dailyHours: {} }] }]
  const [weeklyAllocations, setWeeklyAllocations] = useState([]);

  const weekDays = useMemo(() => {
    return getWeekDays(timesheet?.date || new Date().toISOString().split('T')[0]);
  }, [timesheet?.date]);

  // Group entries by crew member + job combo (each combo = one table row)
  const lineGroups = useMemo(() => {
    const groups = {};
    (laborEntries || []).forEach(entry => {
      const key = `${entry.crewMemberId}|${entry.jobId}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          crewMemberId: entry.crewMemberId,
          jobId: entry.jobId,
          craftId: entry.craftId,
          classificationId: entry.classificationId,
          entries: []
        };
      }
      groups[key].entries.push(entry);
    });
    return Object.values(groups);
  }, [laborEntries]);

  const getCrewName = useCallback((id) => {
    const m = crewMembers.find(c => c.id === id);
    return m ? m.fullName : 'Unknown';
  }, [crewMembers]);

  const getCraftName = useCallback((id) => {
    const c = crafts.find(cr => cr.id === id);
    return c ? c.name : '';
  }, [crafts]);

  const getClassName = useCallback((id) => {
    const c = classifications.find(cl => cl.id === id);
    return c ? c.name : '';
  }, [classifications]);

  const getJobNumber = useCallback((id) => {
    const j = jobs.find(job => job.id === id);
    return j ? j.jobNumber : '';
  }, [jobs]);

  const filteredCrewForCraft = useMemo(() => {
    if (!formCraftId) return [];
    return crewMembers.filter(m => m.eligibleCraftIds?.includes(formCraftId));
  }, [formCraftId, crewMembers]);

  const initDailyHours = useCallback(() => {
    const hours = {};
    weekDays.forEach(day => {
      hours[day.date] = { hoursST: '', hoursOT: '', hoursDT: '' };
    });
    return hours;
  }, [weekDays]);

  const openAddDialog = () => {
    setEditingLineKey(null);
    setBulkMode(true);
    setFormCraftId('');
    setFormCrewMemberId('');
    setFormJobId('');
    setFormClassificationId('');
    setDailyHours(initDailyHours());
    setWeeklyAllocations([]);
    setCrewSearchFilter('');
    setDialogOpen(true);
  };

  const openEditDialog = (lineKey) => {
    setEditingLineKey(lineKey);
    setBulkMode(false);
    const group = lineGroups.find(g => g.key === lineKey);
    if (!group || group.entries.length === 0) return;

    const first = group.entries[0];
    setFormCraftId(first.craftId || '');
    setFormCrewMemberId(group.crewMemberId);
    setFormJobId(group.jobId);
    setFormClassificationId(first.classificationId || '');

    const hours = initDailyHours();
    group.entries.forEach(entry => {
      const d = entry.entryDate;
      if (d && hours[d]) {
        hours[d] = {
          hoursST: entry.hoursST > 0 ? String(entry.hoursST) : '',
          hoursOT: entry.hoursOT > 0 ? String(entry.hoursOT) : '',
          hoursDT: entry.hoursDT > 0 ? String(entry.hoursDT) : '',
        };
      }
    });
    setDailyHours(hours);
    setDialogOpen(true);
  };

  const updateDayHours = (date, field, value) => {
    setDailyHours(prev => ({
      ...prev,
      [date]: { ...prev[date], [field]: value }
    }));
  };

  // Per-person allocation helpers
  const addCrewToAllocations = (crewId) => {
    if (weeklyAllocations.some(a => a.crewMemberId === crewId)) return;
    setWeeklyAllocations(prev => [...prev, {
      crewMemberId: crewId,
      craftId: formCraftId,
      jobs: [{ jobId: '', classificationId: '', dailyHours: initDailyHours() }]
    }]);
  };

  const removeCrewFromAllocations = (crewId) => {
    setWeeklyAllocations(prev => prev.filter(a => a.crewMemberId !== crewId));
  };

  const toggleCrewSelection = (crewId) => {
    if (weeklyAllocations.some(a => a.crewMemberId === crewId)) {
      removeCrewFromAllocations(crewId);
    } else {
      addCrewToAllocations(crewId);
    }
  };

  const selectAllCrew = () => {
    const eligible = filteredCrewForCraft;
    const existingIds = new Set(weeklyAllocations.map(a => a.crewMemberId));
    const newAllocs = eligible
      .filter(m => !existingIds.has(m.id))
      .map(m => ({
        crewMemberId: m.id,
        craftId: formCraftId,
        jobs: [{ jobId: '', classificationId: '', dailyHours: initDailyHours() }]
      }));
    setWeeklyAllocations(prev => [...prev, ...newAllocs]);
  };

  const removeAllocByIndex = (allocIndex) => {
    setWeeklyAllocations(prev => prev.filter((_, i) => i !== allocIndex));
  };

  const addJobRowToAlloc = (allocIndex) => {
    setWeeklyAllocations(prev => {
      const updated = [...prev];
      updated[allocIndex] = {
        ...updated[allocIndex],
        jobs: [...updated[allocIndex].jobs, { jobId: '', classificationId: '', dailyHours: initDailyHours() }]
      };
      return updated;
    });
  };

  const removeJobRowFromAlloc = (allocIndex, jobIndex) => {
    setWeeklyAllocations(prev => {
      const updated = [...prev];
      const newJobs = updated[allocIndex].jobs.filter((_, ji) => ji !== jobIndex);
      if (newJobs.length === 0) return prev.filter((_, i) => i !== allocIndex);
      updated[allocIndex] = { ...updated[allocIndex], jobs: newJobs };
      return updated;
    });
  };

  const updateAllocJobField = (allocIndex, jobIndex, field, value) => {
    setWeeklyAllocations(prev => {
      const updated = [...prev];
      const newJobs = [...updated[allocIndex].jobs];
      newJobs[jobIndex] = { ...newJobs[jobIndex], [field]: value };
      updated[allocIndex] = { ...updated[allocIndex], jobs: newJobs };
      return updated;
    });
  };

  const updateAllocDayHours = (allocIndex, jobIndex, date, field, value) => {
    setWeeklyAllocations(prev => {
      const updated = [...prev];
      const newJobs = [...updated[allocIndex].jobs];
      const newDH = { ...newJobs[jobIndex].dailyHours, [date]: { ...newJobs[jobIndex].dailyHours[date], [field]: value } };
      newJobs[jobIndex] = { ...newJobs[jobIndex], dailyHours: newDH };
      updated[allocIndex] = { ...updated[allocIndex], jobs: newJobs };
      return updated;
    });
  };

  const applyJobToAll = (jobId) => {
    setWeeklyAllocations(prev => prev.map(a => ({
      ...a,
      jobs: a.jobs.map(j => ({ ...j, jobId }))
    })));
  };

  const selectedCrewIds = weeklyAllocations.map(a => a.crewMemberId);

  const handleSubmit = async () => {
    if (bulkMode) {
      // Per-person multi-job submit
      if (weeklyAllocations.length === 0) {
        toast.error('Select at least one crew member');
        return;
      }

      // Collect all valid entries
      const allEntries = [];
      for (const alloc of weeklyAllocations) {
        for (const job of alloc.jobs) {
          if (!job.jobId || !job.classificationId) continue;
          const daysWithHours = Object.entries(job.dailyHours).filter(([_, h]) => {
            return (parseFloat(h.hoursST) || 0) + (parseFloat(h.hoursOT) || 0) + (parseFloat(h.hoursDT) || 0) > 0;
          });
          for (const [date, hours] of daysWithHours) {
            allEntries.push({
              jobId: job.jobId,
              crewMemberId: alloc.crewMemberId,
              craftId: alloc.craftId,
              classificationId: job.classificationId,
              hoursST: parseFloat(hours.hoursST) || 0,
              hoursOT: parseFloat(hours.hoursOT) || 0,
              hoursDT: parseFloat(hours.hoursDT) || 0,
              entryDate: date,
            });
          }
        }
      }

      if (allEntries.length === 0) {
        toast.error('Please fill in job, classification, and hours for at least one crew member');
        return;
      }

      setSubmitting(true);
      try {
        for (const entry of allEntries) {
          await axios.post(`${API}/timesheets/${timesheet.id}/labor-entries`, entry, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        toast.success(`${allEntries.length} entries created for ${weeklyAllocations.length} crew members`);
        setDialogOpen(false);
        onRefresh();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save entries');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Single line mode (existing edit logic)
    if (!formCrewMemberId || !formJobId || !formClassificationId || !formCraftId) {
      toast.error('Please fill in all required fields');
      return;
    }

    const daysWithHours = Object.entries(dailyHours).filter(([_, h]) => {
      return (parseFloat(h.hoursST) || 0) + (parseFloat(h.hoursOT) || 0) + (parseFloat(h.hoursDT) || 0) > 0;
    });

    if (daysWithHours.length === 0) {
      toast.error('Please enter hours for at least one day');
      return;
    }

    setSubmitting(true);
    try {
      // If editing, delete existing entries for this crew+job combo
      if (editingLineKey) {
        const group = lineGroups.find(g => g.key === editingLineKey);
        if (group) {
          for (const entry of group.entries) {
            await axios.delete(`${API}/labor-entries/${entry.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        }
      }

      // Create entries for each day with hours
      for (const [date, hours] of daysWithHours) {
        await axios.post(`${API}/timesheets/${timesheet.id}/labor-entries`, {
          jobId: formJobId,
          crewMemberId: formCrewMemberId,
          craftId: formCraftId,
          classificationId: formClassificationId,
          hoursST: parseFloat(hours.hoursST) || 0,
          hoursOT: parseFloat(hours.hoursOT) || 0,
          hoursDT: parseFloat(hours.hoursDT) || 0,
          entryDate: date,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }

      toast.success(editingLineKey ? 'Line updated' : `${daysWithHours.length} entries created`);
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entries');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLine = async (lineKey) => {
    const group = lineGroups.find(g => g.key === lineKey);
    if (!group) return;

    try {
      for (const entry of group.entries) {
        await axios.delete(`${API}/labor-entries/${entry.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Line removed');
      setDeletingLineKey(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete entries');
    }
  };

  const getDayHours = (entries, date) => {
    const entry = entries.find(e => e.entryDate === date);
    if (!entry) return null;
    return { st: entry.hoursST || 0, ot: entry.hoursOT || 0, dt: entry.hoursDT || 0 };
  };

  const lineTotals = (entries) => {
    return entries.reduce((acc, e) => ({
      st: acc.st + (e.hoursST || 0),
      ot: acc.ot + (e.hoursOT || 0),
      dt: acc.dt + (e.hoursDT || 0),
      charge: acc.charge + (e.totalCharge || 0),
    }), { st: 0, ot: 0, dt: 0, charge: 0 });
  };

  const grandTotals = useMemo(() => {
    return (laborEntries || []).reduce((acc, e) => ({
      st: acc.st + (e.hoursST || 0),
      ot: acc.ot + (e.hoursOT || 0),
      dt: acc.dt + (e.hoursDT || 0),
      charge: acc.charge + (e.totalCharge || 0),
    }), { st: 0, ot: 0, dt: 0, charge: 0 });
  }, [laborEntries]);

  return (
    <div data-testid='weekly-grid-container'>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-3'>
          <h3 className='text-lg font-bold text-slate-900'>Labor Entries</h3>
          <Badge className='bg-indigo-100 text-indigo-700 rounded-sm'>WEEKLY</Badge>
          <span className='text-sm text-slate-500'>
            {lineGroups.length} {lineGroups.length === 1 ? 'line' : 'lines'} &middot; {(laborEntries || []).length} entries
          </span>
        </div>
        {canEdit && (
          <Button onClick={openAddDialog} data-testid='add-weekly-entry-btn'
            className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'>
            <Plus className='w-4 h-4 mr-1' /> Add Line
          </Button>
        )}
      </div>

      {/* Totals Summary */}
      <div className='grid grid-cols-4 gap-3 mb-4'>
        <div className='bg-slate-50 border border-slate-200 rounded-sm p-3 text-center'>
          <p className='text-xs font-bold uppercase text-slate-400'>ST Hours</p>
          <p className='text-xl font-bold text-slate-900'>{grandTotals.st.toFixed(1)}</p>
        </div>
        <div className='bg-slate-50 border border-slate-200 rounded-sm p-3 text-center'>
          <p className='text-xs font-bold uppercase text-slate-400'>OT Hours</p>
          <p className='text-xl font-bold text-amber-600'>{grandTotals.ot.toFixed(1)}</p>
        </div>
        <div className='bg-slate-50 border border-slate-200 rounded-sm p-3 text-center'>
          <p className='text-xs font-bold uppercase text-slate-400'>DT Hours</p>
          <p className='text-xl font-bold text-red-600'>{grandTotals.dt.toFixed(1)}</p>
        </div>
        <div className='bg-[#1A3668]/5 border border-[#1A3668]/20 rounded-sm p-3 text-center'>
          <p className='text-xs font-bold uppercase text-[#1A3668]/60'>Total Charge</p>
          <p className='text-xl font-bold text-[#1A3668]'>${grandTotals.charge.toFixed(2)}</p>
        </div>
      </div>

      {/* Table */}
      {lineGroups.length > 0 ? (
        <div className='border border-slate-200 rounded-sm overflow-x-auto'>
          <table className='w-full text-sm' data-testid='weekly-grid-table'>
            <thead>
              <tr className='bg-slate-50 border-b border-slate-200'>
                <th className='text-left p-3 font-bold text-xs uppercase tracking-wider text-slate-500 min-w-[140px] sticky left-0 bg-slate-50 z-10'>Name</th>
                <th className='text-left p-2 font-bold text-xs uppercase tracking-wider text-slate-500 min-w-[70px]'>Craft</th>
                <th className='text-left p-2 font-bold text-xs uppercase tracking-wider text-slate-500 min-w-[70px]'>Class</th>
                <th className='text-left p-2 font-bold text-xs uppercase tracking-wider text-slate-500 min-w-[80px]'>Job</th>
                {weekDays.map(day => (
                  <th key={day.date} className='text-center p-2 font-bold text-xs uppercase tracking-wider text-slate-500 min-w-[60px]'>
                    <div>{day.label}</div>
                    <div className='text-[10px] text-slate-400 font-normal'>{day.shortDate}</div>
                  </th>
                ))}
                <th className='text-center p-2 font-bold text-xs uppercase tracking-wider text-slate-500 min-w-[90px] bg-slate-100'>Total</th>
                {canEdit && <th className='p-2 w-[70px]'></th>}
              </tr>
            </thead>
            <tbody>
              {lineGroups.map((line) => {
                const totals = lineTotals(line.entries);
                return (
                  <tr key={line.key} data-testid={`line-row-${line.key}`} className='border-b border-slate-100 hover:bg-slate-50/50'>
                    <td className='p-3 font-bold text-[#1A3668] text-sm sticky left-0 bg-white z-10'>{getCrewName(line.crewMemberId)}</td>
                    <td className='p-2 text-xs text-slate-600'>{getCraftName(line.craftId)}</td>
                    <td className='p-2 text-xs text-slate-600'>{getClassName(line.classificationId)}</td>
                    <td className='p-2 text-xs text-slate-700 font-medium'>{getJobNumber(line.jobId)}</td>
                    {weekDays.map(day => {
                      const h = getDayHours(line.entries, day.date);
                      return (
                        <td key={day.date} className='p-2 text-center text-xs'>
                          {h ? (
                            <>
                              {h.st > 0 && <div className='font-medium text-slate-900'>{h.st}</div>}
                              {h.ot > 0 && <div className='text-[10px] text-amber-600'>+{h.ot}OT</div>}
                              {h.dt > 0 && <div className='text-[10px] text-red-600'>+{h.dt}DT</div>}
                              {h.st === 0 && h.ot === 0 && h.dt === 0 && <span className='text-slate-300'>-</span>}
                            </>
                          ) : (
                            <span className='text-slate-300'>-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className='p-2 text-center bg-slate-50'>
                      <div className='text-xs font-bold text-slate-900'>{totals.st.toFixed(1)}</div>
                      {totals.ot > 0 && <div className='text-[10px] text-amber-600'>{totals.ot.toFixed(1)} OT</div>}
                      {totals.dt > 0 && <div className='text-[10px] text-red-600'>{totals.dt.toFixed(1)} DT</div>}
                      <div className='text-[10px] font-bold text-[#1A3668] mt-0.5'>${totals.charge.toFixed(0)}</div>
                    </td>
                    {canEdit && (
                      <td className='p-2'>
                        <div className='flex items-center gap-1'>
                          <Button variant='ghost' size='sm' onClick={() => openEditDialog(line.key)}
                            data-testid={`edit-line-${line.key}`}
                            className='h-7 w-7 p-0 text-slate-400 hover:text-[#1A3668]'>
                            <Pencil className='w-3.5 h-3.5' />
                          </Button>
                          <Button variant='ghost' size='sm' onClick={() => setDeletingLineKey(line.key)}
                            data-testid={`delete-line-${line.key}`}
                            className='h-7 w-7 p-0 text-slate-400 hover:text-red-600'>
                            <Trash2 className='w-3.5 h-3.5' />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {/* Grand Total Row */}
              <tr className='bg-slate-100 font-bold'>
                <td className='p-3 text-xs uppercase text-slate-500 sticky left-0 bg-slate-100 z-10' colSpan={4}>Grand Total</td>
                {weekDays.map(day => {
                  const dayEntries = (laborEntries || []).filter(e => e.entryDate === day.date);
                  const dayST = dayEntries.reduce((s, e) => s + (e.hoursST || 0), 0);
                  const dayOT = dayEntries.reduce((s, e) => s + (e.hoursOT || 0), 0);
                  const dayDT = dayEntries.reduce((s, e) => s + (e.hoursDT || 0), 0);
                  return (
                    <td key={day.date} className='p-2 text-center text-xs'>
                      {dayST > 0 && <div className='text-slate-900'>{dayST}</div>}
                      {dayOT > 0 && <div className='text-amber-600'>{dayOT}</div>}
                      {dayDT > 0 && <div className='text-red-600'>{dayDT}</div>}
                      {dayST === 0 && dayOT === 0 && dayDT === 0 && <span className='text-slate-300'>-</span>}
                    </td>
                  );
                })}
                <td className='p-2 text-center bg-slate-200'>
                  <div className='text-xs text-slate-900'>{grandTotals.st.toFixed(1)} ST</div>
                  {grandTotals.ot > 0 && <div className='text-[10px] text-amber-600'>{grandTotals.ot.toFixed(1)} OT</div>}
                  {grandTotals.dt > 0 && <div className='text-[10px] text-red-600'>{grandTotals.dt.toFixed(1)} DT</div>}
                </td>
                {canEdit && <td></td>}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className='border border-dashed border-slate-300 rounded-sm p-8 text-center'>
          <Users className='w-10 h-10 mx-auto text-slate-400 mb-3' />
          <p className='text-slate-500 font-medium'>No labor entries yet</p>
          {canEdit && (
            <Button onClick={openAddDialog} className='mt-4 bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm'>
              <Plus className='w-4 h-4 mr-1' /> Add Line
            </Button>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      {deletingLineKey && (
        <Dialog open={!!deletingLineKey} onOpenChange={() => setDeletingLineKey(null)}>
          <DialogContent className='rounded-sm'>
            <DialogHeader>
              <DialogTitle>Delete Line?</DialogTitle>
              <DialogDescription>
                Remove all entries for this crew member / job combination? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant='outline' onClick={() => setDeletingLineKey(null)} className='rounded-sm'>Cancel</Button>
              <Button onClick={() => handleDeleteLine(deletingLineKey)} className='bg-red-600 hover:bg-red-700 text-white rounded-sm'>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add/Edit Line Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm' data-testid='weekly-entry-dialog'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold text-slate-900'>
              {editingLineKey ? 'Edit Line' : 'Add Lines'}
            </DialogTitle>
            <DialogDescription>
              {editingLineKey
                ? 'Update hours for this crew member and job'
                : 'Select a craft to see crew, check members, then assign each person their job(s) and daily hours. Switch crafts to add more.'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-5'>
            {/* Craft Selection */}
            <div>
              <Label className='text-xs font-bold uppercase text-slate-500'>Craft *</Label>
              <Select value={formCraftId} onValueChange={(v) => {
                setFormCraftId(v);
                if (!editingLineKey) { setFormCrewMemberId(''); setFormClassificationId(''); }
              }} disabled={!!editingLineKey}>
                <SelectTrigger data-testid='weekly-craft-select' className='mt-1 rounded-sm border-2'>
                  <SelectValue placeholder='Select craft...' />
                </SelectTrigger>
                <SelectContent>
                  {crafts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Mode: Checkbox Crew Selector */}
            {bulkMode && formCraftId && (
              <div className='border border-slate-200 rounded-sm'>
                <div className='flex items-center justify-between p-3 bg-slate-50 border-b border-slate-200'>
                  <div>
                    <h4 className='text-sm font-bold uppercase tracking-wider text-slate-700'>
                      Select {getCraftName(formCraftId)} Crew
                    </h4>
                    {weeklyAllocations.length > 0 && (
                      <p className='text-xs text-slate-500 mt-0.5'>{weeklyAllocations.length} total selected across all crafts</p>
                    )}
                  </div>
                  <Button type='button' variant='outline' size='sm' onClick={selectAllCrew}
                    className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-100 text-xs h-7'
                    data-testid='weekly-crew-select-all'>
                    <Users className='w-3.5 h-3.5 mr-1' /> Select All
                  </Button>
                </div>
                <div className='px-3 pt-2'>
                  <div className='relative'>
                    <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400' />
                    <Input data-testid='weekly-crew-search' value={crewSearchFilter}
                      onChange={(e) => setCrewSearchFilter(e.target.value)}
                      placeholder='Search crew...' className='pl-8 h-8 rounded-sm border-2 text-sm' />
                  </div>
                </div>
                <div className='p-2 max-h-40 overflow-y-auto'>
                  {filteredCrewForCraft
                    .filter(m => !crewSearchFilter.trim() || m.fullName.toLowerCase().includes(crewSearchFilter.toLowerCase()))
                    .map((member) => {
                      const isSelected = selectedCrewIds.includes(member.id);
                      return (
                        <label key={member.id} data-testid={`weekly-crew-checkbox-${member.id}`}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-[#1A3668]/5' : 'hover:bg-slate-50'}`}>
                          <Checkbox checked={isSelected} onCheckedChange={() => toggleCrewSelection(member.id)} className='border-2' />
                          <span className={`text-sm font-medium ${isSelected ? 'text-[#1A3668]' : 'text-slate-700'}`}>
                            {member.fullName}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Edit Mode: Single crew member selector */}
            {!bulkMode && (
              <div>
                <Label className='text-xs font-bold uppercase text-slate-500'>Crew Member *</Label>
                <Select value={formCrewMemberId} onValueChange={setFormCrewMemberId} disabled={!formCraftId || !!editingLineKey}>
                  <SelectTrigger data-testid='weekly-crew-select' className='mt-1 rounded-sm border-2'>
                    <SelectValue placeholder='Select crew member...' />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCrewForCraft.map(m => <SelectItem key={m.id} value={m.id}>{m.fullName}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Edit Mode: Classification + Job + Hours */}
            {!bulkMode && (
              <>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <Label className='text-xs font-bold uppercase text-slate-500'>Classification *</Label>
                    <Select value={formClassificationId} onValueChange={setFormClassificationId} disabled={!formCraftId}>
                      <SelectTrigger data-testid='weekly-class-select' className='mt-1 rounded-sm border-2'>
                        <SelectValue placeholder='Select classification...' />
                      </SelectTrigger>
                      <SelectContent>
                        {(getFilteredClassifications ? getFilteredClassifications(formCraftId) : classifications.filter(c => c.craftId === formCraftId)).map(cls => (
                          <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className='text-xs font-bold uppercase text-slate-500'>Job *</Label>
                    <Select value={formJobId} onValueChange={setFormJobId}>
                      <SelectTrigger data-testid='weekly-job-select' className='mt-1 rounded-sm border-2'>
                        <SelectValue placeholder='Select job...' />
                      </SelectTrigger>
                      <SelectContent>
                        {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.jobNumber} - {j.description}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Edit mode daily hours grid */}
                <WeeklyHoursGrid weekDays={weekDays} dailyHours={dailyHours} updateFn={updateDayHours} label='' />
              </>
            )}

            {/* Bulk Mode: Per-Person Allocations with Multi-Job */}
            {bulkMode && weeklyAllocations.length > 0 && (
              <div className='border border-slate-200 rounded-sm'>
                <div className='flex items-center justify-between p-3 bg-slate-50 border-b border-slate-200'>
                  <h4 className='text-sm font-bold uppercase tracking-wider text-slate-700'>
                    Crew Allocations ({weeklyAllocations.length} members, {weeklyAllocations.reduce((s, a) => s + a.jobs.length, 0)} job rows)
                  </h4>
                  <Select onValueChange={applyJobToAll}>
                    <SelectTrigger data-testid='weekly-apply-all-job' className='w-[200px] rounded-sm border-2 bg-white h-8 text-xs'>
                      <SelectValue placeholder='Set Job for all...' />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.jobNumber} - {j.description}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className='p-3 space-y-4'>
                  {weeklyAllocations.map((alloc, allocIndex) => {
                    const member = crewMembers.find(m => m.id === alloc.crewMemberId);
                    return (
                      <div key={alloc.crewMemberId} data-testid={`weekly-alloc-${allocIndex}`} className='border border-slate-200 rounded-sm'>
                        <div className='flex items-center justify-between p-3 bg-slate-50/50 border-b border-slate-100'>
                          <div className='flex items-center gap-2'>
                            <span className='font-bold text-[#1A3668] text-sm'>{member?.fullName || 'Unknown'}</span>
                            <span className='text-xs bg-[#1A3668]/10 text-[#1A3668] px-1.5 py-0.5 rounded font-medium'>
                              {getCraftName(alloc.craftId)}
                            </span>
                          </div>
                          <div className='flex items-center gap-2'>
                            <Button type='button' onClick={() => addJobRowToAlloc(allocIndex)} variant='outline' size='sm'
                              data-testid={`weekly-add-job-${allocIndex}`}
                              className='rounded-sm border-slate-200 text-[#1A3668] hover:bg-slate-100 h-7 text-xs'>
                              <Plus className='w-3 h-3 mr-1' /> Add Job
                            </Button>
                            <Button type='button' onClick={() => removeAllocByIndex(allocIndex)} variant='ghost' size='sm'
                              className='text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0'>
                              <X className='w-4 h-4' />
                            </Button>
                          </div>
                        </div>
                        <div className='p-3 space-y-3'>
                          {alloc.jobs.map((job, jobIndex) => (
                            <div key={job.jobId || `job-${jobIndex}`} data-testid={`weekly-alloc-${allocIndex}-job-${jobIndex}`} className='border border-slate-100 rounded-sm'>
                              <div className='p-2 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between'>
                                <div className='grid grid-cols-2 gap-2 flex-1'>
                                  <Select value={job.jobId} onValueChange={(v) => updateAllocJobField(allocIndex, jobIndex, 'jobId', v)}>
                                    <SelectTrigger className='rounded-sm border-2 h-8 text-xs'>
                                      <SelectValue placeholder='Select Job...' />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.jobNumber} - {j.description}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <Select value={job.classificationId} onValueChange={(v) => updateAllocJobField(allocIndex, jobIndex, 'classificationId', v)}>
                                    <SelectTrigger className='rounded-sm border-2 h-8 text-xs'>
                                      <SelectValue placeholder='Select Class...' />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(getFilteredClassifications ? getFilteredClassifications(alloc.craftId) : classifications.filter(c => c.craftId === alloc.craftId)).map(cls => (
                                        <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                {alloc.jobs.length > 1 && (
                                  <Button type='button' onClick={() => removeJobRowFromAlloc(allocIndex, jobIndex)} variant='ghost' size='sm'
                                    className='text-red-400 hover:text-red-600 ml-2 h-8 w-8 p-0'>
                                    <Trash2 className='w-3.5 h-3.5' />
                                  </Button>
                                )}
                              </div>
                              {/* Per-job daily hours grid with ST/OT/DT rows */}
                              <div className='overflow-x-auto'>
                                <table className='w-full text-xs'>
                                  <thead>
                                    <tr className='border-b border-slate-100'>
                                      <th className='p-1 text-left text-slate-500 w-8'></th>
                                      {weekDays.map(day => (
                                        <th key={day.date} className='p-1 text-center'>
                                          <div className='font-bold text-slate-600'>{day.label}</div>
                                          <div className='text-[9px] text-slate-400'>{day.shortDate}</div>
                                        </th>
                                      ))}
                                      <th className='p-1 text-center text-slate-500 w-14'>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* ST Row */}
                                    <tr>
                                      <td className='p-1 text-xs font-bold text-slate-500'>ST</td>
                                      {weekDays.map(day => {
                                        const h = job.dailyHours[day.date] || { hoursST: '', hoursOT: '', hoursDT: '' };
                                        return (
                                          <td key={day.date} className='p-1'>
                                            <Input type='number' step='0.5' min='0' placeholder='0'
                                              value={h.hoursST}
                                              onChange={(e) => updateAllocDayHours(allocIndex, jobIndex, day.date, 'hoursST', e.target.value)}
                                              className='text-center h-7 rounded-sm border text-xs w-full' />
                                          </td>
                                        );
                                      })}
                                      <td className='p-1 text-center'>
                                        <span className='font-bold text-slate-700 text-xs'>
                                          {weekDays.reduce((s, d) => s + (parseFloat((job.dailyHours[d.date] || {}).hoursST) || 0), 0).toFixed(1)}
                                        </span>
                                      </td>
                                    </tr>
                                    {/* OT Row */}
                                    <tr className='bg-amber-50/30'>
                                      <td className='p-1 text-xs font-bold text-amber-600'>OT</td>
                                      {weekDays.map(day => {
                                        const h = job.dailyHours[day.date] || { hoursST: '', hoursOT: '', hoursDT: '' };
                                        return (
                                          <td key={day.date} className='p-1'>
                                            <Input type='number' step='0.5' min='0' placeholder='0'
                                              value={h.hoursOT}
                                              onChange={(e) => updateAllocDayHours(allocIndex, jobIndex, day.date, 'hoursOT', e.target.value)}
                                              className='text-center h-7 rounded-sm border text-xs w-full' />
                                          </td>
                                        );
                                      })}
                                      <td className='p-1 text-center'>
                                        <span className='font-bold text-amber-600 text-xs'>
                                          {weekDays.reduce((s, d) => s + (parseFloat((job.dailyHours[d.date] || {}).hoursOT) || 0), 0).toFixed(1)}
                                        </span>
                                      </td>
                                    </tr>
                                    {/* DT Row */}
                                    <tr className='bg-red-50/30'>
                                      <td className='p-1 text-xs font-bold text-red-600'>DT</td>
                                      {weekDays.map(day => {
                                        const h = job.dailyHours[day.date] || { hoursST: '', hoursOT: '', hoursDT: '' };
                                        return (
                                          <td key={day.date} className='p-1'>
                                            <Input type='number' step='0.5' min='0' placeholder='0'
                                              value={h.hoursDT}
                                              onChange={(e) => updateAllocDayHours(allocIndex, jobIndex, day.date, 'hoursDT', e.target.value)}
                                              className='text-center h-7 rounded-sm border text-xs w-full' />
                                          </td>
                                        );
                                      })}
                                      <td className='p-1 text-center'>
                                        <span className='font-bold text-red-600 text-xs'>
                                          {weekDays.reduce((s, d) => s + (parseFloat((job.dailyHours[d.date] || {}).hoursDT) || 0), 0).toFixed(1)}
                                        </span>
                                      </td>
                                    </tr>
                                    {/* Day Totals Row */}
                                    <tr className='border-t border-slate-200 bg-slate-50'>
                                      <td className='p-1 text-xs font-bold text-slate-500'>Tot</td>
                                      {weekDays.map(day => {
                                        const h = job.dailyHours[day.date] || {};
                                        const dayTotal = (parseFloat(h.hoursST) || 0) + (parseFloat(h.hoursOT) || 0) + (parseFloat(h.hoursDT) || 0);
                                        return (
                                          <td key={day.date} className='p-1 text-center'>
                                            <span className={`font-bold text-xs ${dayTotal > 0 ? 'text-[#1A3668]' : 'text-slate-300'}`}>
                                              {dayTotal > 0 ? dayTotal.toFixed(1) : '-'}
                                            </span>
                                          </td>
                                        );
                                      })}
                                      <td className='p-1 text-center'>
                                        <span className='font-bold text-[#1A3668] text-xs'>
                                          {weekDays.reduce((s, d) => {
                                            const h = job.dailyHours[d.date] || {};
                                            return s + (parseFloat(h.hoursST) || 0) + (parseFloat(h.hoursOT) || 0) + (parseFloat(h.hoursDT) || 0);
                                          }, 0).toFixed(1)}
                                        </span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty state */}
            {bulkMode && weeklyAllocations.length === 0 && formCraftId && (
              <div className='border border-dashed border-slate-300 rounded-sm p-6 text-center'>
                <Users className='w-8 h-8 mx-auto text-slate-400 mb-2' />
                <p className='text-sm text-slate-500'>Check crew members above to assign hours</p>
              </div>
            )}
          </div>

          <DialogFooter className='mt-4'>
            <Button variant='outline' onClick={() => setDialogOpen(false)} className='rounded-sm border-2'>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid='weekly-submit-btn'
              className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'>
              {submitting ? 'Saving...' : editingLineKey ? 'Update Line' : `Add ${weeklyAllocations.reduce((s, a) => s + a.jobs.length, 0) || ''} Lines`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Compact weekly hours grid for edit mode
function WeeklyHoursGrid({ weekDays, dailyHours, updateFn, label }) {
  return (
    <div className='border border-slate-200 rounded-sm'>
      {label && (
        <div className='p-3 bg-slate-50 border-b border-slate-200'>
          <h4 className='text-xs font-bold uppercase tracking-wider text-slate-500'>{label}</h4>
        </div>
      )}
      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b border-slate-200'>
              <th className='text-left p-2 text-xs font-bold uppercase text-slate-400 w-16'>Day</th>
              <th className='text-center p-2 text-xs font-bold uppercase text-slate-400'>ST</th>
              <th className='text-center p-2 text-xs font-bold uppercase text-slate-400'>OT</th>
              <th className='text-center p-2 text-xs font-bold uppercase text-slate-400'>DT</th>
              <th className='text-center p-2 text-xs font-bold uppercase text-slate-400 w-20'>Total</th>
            </tr>
          </thead>
          <tbody>
            {weekDays.map((day) => {
              const h = dailyHours[day.date] || { hoursST: '', hoursOT: '', hoursDT: '' };
              const dayTotal = (parseFloat(h.hoursST) || 0) + (parseFloat(h.hoursOT) || 0) + (parseFloat(h.hoursDT) || 0);
              const isWeekend = day.label === 'Sat' || day.label === 'Sun';
              return (
                <tr key={day.date} className={`border-b border-slate-100 ${isWeekend ? 'bg-amber-50/30' : ''}`}>
                  <td className='p-2'>
                    <div className='font-bold text-slate-700'>{day.label}</div>
                    <div className='text-[10px] text-slate-400'>{day.shortDate}</div>
                  </td>
                  <td className='p-2'>
                    <Input data-testid={`day-st-${day.date}`} type='number' step='0.5' min='0' placeholder='0'
                      value={h.hoursST} onChange={(e) => updateFn(day.date, 'hoursST', e.target.value)}
                      className='text-center h-9 rounded-sm border-2 text-sm' />
                  </td>
                  <td className='p-2'>
                    <Input data-testid={`day-ot-${day.date}`} type='number' step='0.5' min='0' placeholder='0'
                      value={h.hoursOT} onChange={(e) => updateFn(day.date, 'hoursOT', e.target.value)}
                      className='text-center h-9 rounded-sm border-2 text-sm' />
                  </td>
                  <td className='p-2'>
                    <Input data-testid={`day-dt-${day.date}`} type='number' step='0.5' min='0' placeholder='0'
                      value={h.hoursDT} onChange={(e) => updateFn(day.date, 'hoursDT', e.target.value)}
                      className='text-center h-9 rounded-sm border-2 text-sm' />
                  </td>
                  <td className='p-2 text-center'>
                    <span className={`font-bold text-sm ${dayTotal > 0 ? 'text-[#1A3668]' : 'text-slate-300'}`}>
                      {dayTotal > 0 ? dayTotal.toFixed(1) : '-'}
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr className='bg-slate-100 font-bold'>
              <td className='p-2 text-xs uppercase text-slate-500'>Total</td>
              <td className='p-2 text-center text-sm text-slate-900'>
                {Object.values(dailyHours).reduce((s, h) => s + (parseFloat(h.hoursST) || 0), 0).toFixed(1)}
              </td>
              <td className='p-2 text-center text-sm text-amber-600'>
                {Object.values(dailyHours).reduce((s, h) => s + (parseFloat(h.hoursOT) || 0), 0).toFixed(1)}
              </td>
              <td className='p-2 text-center text-sm text-red-600'>
                {Object.values(dailyHours).reduce((s, h) => s + (parseFloat(h.hoursDT) || 0), 0).toFixed(1)}
              </td>
              <td className='p-2 text-center text-sm font-bold text-[#1A3668]'>
                {Object.values(dailyHours).reduce((s, h) => s + (parseFloat(h.hoursST) || 0) + (parseFloat(h.hoursOT) || 0) + (parseFloat(h.hoursDT) || 0), 0).toFixed(1)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
